import { Reports_GetReportTemplateQuery, ReportScheduleRecurrence } from '@/__generated__/graphql';
import { useAuth, useModalStatusMessage } from '@/hooks';
import { ApolloError, skipToken, useMutation, useSuspenseQuery } from '@apollo/client';
import CREATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/createReportTemplate';
import EXECUTE_REPORT from '@features/reports2/graphql/mutation/executeReport';
import UPDATE_REPORT_TEMPLATE from '@features/reports2/graphql/mutation/updateReportTemplate';
import { GET_REPORT_TEMPLATE } from '@features/reports2/graphql/queries/getReportTemplate';
import { parseTimeRange } from '@features/reports2/utils/parseTimeRange';
import { getBrowserTimezone } from '@features/reports2/utils/timezone';
import { zodResolver } from '@hookform/resolvers/zod';
import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { DefaultValues, useForm } from 'react-hook-form';
import { CreateReportFieldValues, CreateReportSchema } from './validation';
import { GET_TEMPLATES } from '@features/reports2/graphql/queries/getReportTemplates';
import { getHoursAndMinutes } from '@features/reports2/utils/schedule';

const now = dayjs();

const getDefaultValues = (
  userEmail: string | null | undefined,
  data?: Reports_GetReportTemplateQuery,
) => {
  const values: DefaultValues<CreateReportFieldValues> = {
    step1: {
      devices: [],
      metrics: [],
    },
    step2: {
      period: {
        start: '-7d',
        stop: null,
      },
      meanWindow: '1h',
      timezone: getBrowserTimezone(),
    },
    step3: {
      createExcel: false,
      createPdf: false,
      emails: userEmail ? [{ email: userEmail }] : [],
    },
    step4: {
      enabled: false,
      schedule: {
        recurrence: ReportScheduleRecurrence.Daily,
        time: now,
      },
      saveTemplate: false,
    },
  };

  if (data) {
    const template = data.reportTemplate;
    values.step1 = {
      devices: template.devices.map((device) => device.id),
      metrics: template.metrics.map((metric) => ({ name: metric.name, unit: metric.unit })),
    };
    values.step2 = {
      meanWindow: template.meanWindow,
      period: { start: template.relativeTime, stop: null },
      timezone: template.timezone,
    };
    values.step3 = {
      createExcel: template.createExcel,
      createPdf: template.createPdf,
      emails: template.emails.map((email) => ({ email })),
    };
    const schedule = template.schedules.at(0);
    const step4Base = {
      saveTemplate: true,
      templateName: template.name,
      templateDescription: template.description ?? undefined,
    };
    if (schedule) {
      const { hours, minutes } = schedule;
      const time = now.set('hours', hours).set('minutes', minutes);
      values.step4 = {
        ...step4Base,
        enabled: true,
        schedule: {
          recurrence: schedule.recurrence,
          dayOfWeek: schedule.dayOfWeek ?? null,
          daysOfWeek: schedule.daysOfWeek ?? null,
          time,
        },
      };
    } else {
      values.step4 = { ...step4Base, enabled: false };
    }
  }

  return values;
};

export const useCreateForm = ({
  templateId,
  onClose,
}: {
  templateId?: number;
  onClose: (skipCloseConfirmation?: boolean) => void;
}) => {
  const { _ } = useLingui();
  const { user } = useAuth();
  const { openModalErrorMessage, openModalSuccessMessage } = useModalStatusMessage();

  const { data } = useSuspenseQuery(
    GET_REPORT_TEMPLATE,
    templateId
      ? {
          variables: {
            id: templateId,
          },
        }
      : skipToken,
  );

  const defaultValues = useMemo(() => {
    return getDefaultValues(user?.email, data);
  }, [data, user]);

  const [createTemplate] = useMutation(CREATE_REPORT_TEMPLATE);
  const [executeReport] = useMutation(EXECUTE_REPORT);
  const [updateReportTemplate] = useMutation(UPDATE_REPORT_TEMPLATE);

  const methods = useForm<CreateReportFieldValues>({
    resolver: zodResolver(CreateReportSchema),
    defaultValues,
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    const period = parseTimeRange(data.step2.period.start, data.step2.period.stop);

    const parameters = {
      onCompleted() {
        onClose(true);
        openModalSuccessMessage(
          _(msg`Se ha encolado la generación del reporte, lo recibirás en unos momentos`),
        );
      },
      onError(error: ApolloError) {
        openModalErrorMessage(
          _(msg`Ha ocurrido un error al ejecutar el reporte`) + ' ' + error.message,
        );
      },
    };

    // Update template
    if (templateId) {
      const relativeTime = period.relativeTime;
      const { templateName, templateDescription } = data.step4;
      if (!relativeTime || !templateName) return;

      const schedule =
        data.step4.enabled && data.step4.schedule ? data.step4.schedule : null;

      await updateReportTemplate({
        variables: {
          input: {
            id: templateId,
            createPdf: data.step3.createPdf,
            createExcel: data.step3.createExcel,
            devices: data.step1.devices,
            emails: data.step3.emails.map(({ email }) => email),
            metrics: data.step1.metrics,
            timezone: data.step2.timezone,
            meanWindow: data.step2.meanWindow,
            name: templateName,
            description: templateDescription || null,
            relativeTime,
            schedule: schedule
              ? {
                  recurrence: schedule.recurrence,
                  timezone: data.step2.timezone,
                  daysOfWeek: schedule.daysOfWeek ?? null,
                  dayOfWeek: schedule.dayOfWeek ?? null,
                  ...getHoursAndMinutes(schedule.time),
                }
              : null,
          },
        },
        onCompleted() {
          onClose(true);
          openModalSuccessMessage(_(msg`Se ha guardado el reporte correctamente`));
        },
        onError(error: ApolloError) {
          openModalErrorMessage(
            _(msg`Ha ocurrido un error al guardar el reporte`) + ' ' + error.message,
          );
        },
        refetchQueries: [GET_TEMPLATES],
      });
    }

    // Only trigger without save
    else if (!data.step4.saveTemplate) {
      await executeReport({
        variables: {
          input: {
            createPdf: data.step3.createPdf,
            createExcel: data.step3.createExcel,
            devices: data.step1.devices,
            emails: data.step3.emails.map(({ email }) => email),
            metrics: data.step1.metrics,
            timezone: data.step2.timezone,
            meanWindow: data.step2.meanWindow,
            ...period,
          },
        },
        ...parameters,
      });
    } else {
      const relativeTime = period.relativeTime;
      const { templateName, templateDescription } = data.step4;
      if (!relativeTime || !templateName) return;

      const schedule =
        data.step4.enabled && data.step4.schedule ? data.step4.schedule : null;

      await createTemplate({
        variables: {
          input: {
            execute: true,
            createPdf: data.step3.createPdf,
            createExcel: data.step3.createExcel,
            devices: data.step1.devices,
            emails: data.step3.emails.map(({ email }) => email),
            metrics: data.step1.metrics,
            timezone: data.step2.timezone,
            meanWindow: data.step2.meanWindow,
            name: templateName,
            description: templateDescription || null,
            relativeTime,
            schedule: schedule
              ? {
                  recurrence: schedule.recurrence,
                  timezone: data.step2.timezone,
                  daysOfWeek: schedule.daysOfWeek ?? null,
                  dayOfWeek: schedule.dayOfWeek ?? null,
                  ...getHoursAndMinutes(schedule.time),
                }
              : null,
          },
        },
        refetchQueries: [GET_TEMPLATES],
        ...parameters,
      });
    }
  });

  return { methods, onSubmit };
};
